import WelcomeImage from '@/assets/images/welcome.svg';
import transitionIllustration from '@/assets/images/Transition_Illustration_workspace.png';
import { useAdminContext } from '@/contexts/AdminContext';
import { useBustlyDesktopOpen, BUSTLY_DOWNLOAD_URL } from '@/hooks/useBustlyDesktopOpen';
import { PluginSetupModal } from '@/components/PluginSetup/PluginSetupModal';
import { getCurrentProductCode, isPluginProduct } from '@common/api/productContext';
import { Button } from '@common/index';
import { useTranslation } from '@client-app/i18n';
import { Cross2Icon } from '@radix-ui/react-icons';
import { ArrowUpRight } from '@phosphor-icons/react';
import React from 'react';
import { createPortal } from 'react-dom';
import styles from './Welcome.module.scss';
import { resolveWorkspaceSuccessPresentation } from './workspaceSuccessPresentation';

interface WelcomeModalProps {
    isOpen: boolean;
    checking?: boolean;
    onClose: () => void;
}

export const ForNextPulseModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
    const { modal } = useAdminContext();
    const { t } = useTranslation();
    
    // Don't show modal if it has been shown before or if not open
    if (!isOpen) return null;

    return createPortal(
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.content}>
                    <div className={styles.leftSection}>
                        <div className={styles.welcomeHeader}>
                            <h2 className={styles.title}>{t('website.welcomeModal.nextPulse.title')}</h2>
                            <p className={styles.benefitsTitle}>
                                {t('website.welcomeModal.nextPulse.description')}
                            </p>
                        </div>
                        
                        <div className={styles.actions}>
                            <Button onClick={onClose} variant="outline">{t('website.welcomeModal.nextPulse.later')}</Button>
                            <Button
                                onClick={() => {
                                    modal.openModal('pricing')
                                    onClose()
                                }}
                            >
                                {t('website.welcomeModal.nextPulse.upgrade')}
                            </Button>
                        </div>
                    </div>
                    
                    <div className={styles.rightSection}>
                        <img src={WelcomeImage} alt={t('website.welcomeModal.nextPulse.imageAlt')} />
                    </div>
                </div>
                <div className={styles.close}>
                    <Cross2Icon onClick={onClose} />
                </div>
            </div>
        </div>,
        document.body
    );
};

const WorkspaceReadyDesktopHandoff: React.FC<Pick<WelcomeModalProps, 'onClose'>> = ({ onClose }) => {
    const { t } = useTranslation();
    const { handleOpenBustlyClick } = useBustlyDesktopOpen({
        onManualComplete: onClose,
    });

    return (
        <>
            <Button
                onClick={() => {
                    void handleOpenBustlyClick();
                }}
                className={styles.verticalOpenButton}
            >
                {t('website.welcomeModal.workspaceReady.openDesktop')}
                <ArrowUpRight size={16} weight="regular" />
            </Button>
            <div className={styles.verticalFooterNote}>
                {t('website.welcomeModal.workspaceReady.downloadQuestion')}
                {' '}
                <a
                    href={BUSTLY_DOWNLOAD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.downloadLink}
                >
                    {t('website.welcomeModal.workspaceReady.downloadApp')}
                </a>
                .
            </div>
        </>
    );
};

const InviteDesktopHandoff: React.FC<Pick<WelcomeModalProps, 'onClose'>> = ({ onClose }) => {
    const { t } = useTranslation();
    const { handleOpenBustlyClick } = useBustlyDesktopOpen({
        onManualComplete: onClose,
    });

    return (
        <>
            <a
                href={BUSTLY_DOWNLOAD_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className={styles.verticalOpenButton}
            >
                {t('website.welcomeModal.inviteSuccess.downloadDesktop')}
                <ArrowUpRight size={16} weight="regular" />
            </a>
            <div className={styles.verticalFooterNote}>
                {t('website.welcomeModal.inviteSuccess.alreadyHaveDesktop')}
                {' '}
                <button
                    type="button"
                    onClick={() => {
                        void handleOpenBustlyClick();
                    }}
                    className={styles.footerInlineButton}
                >
                    {t('website.welcomeModal.inviteSuccess.openApp')}
                </button>
                .
            </div>
        </>
    );
};

export const PaySuccessModal: React.FC<WelcomeModalProps> = ({
    isOpen, checking = false, onClose
}) => {
    const { t } = useTranslation();
    const { modal } = useAdminContext();

    if (isPluginProduct(getCurrentProductCode())) {
        return (
            <PluginSetupModal
                isOpen={isOpen && !checking}
                onClose={() => {
                    onClose();
                    modal.requestPluginSetupSidebarHint();
                }}
            />
        );
    }

    const presentation = resolveWorkspaceSuccessPresentation(getCurrentProductCode());
    const workspaceReadyCaption = t(presentation.workspaceReadyCaptionKey);

    // Don't show modal if it has been shown before or if not open
    if (!isOpen) return null;
    return createPortal(
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.verticalModalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.verticalContent}>
                    <div className={styles.verticalHeader}>
                        <h2 className={styles.verticalTitle}>
                            {checking ? (
                                t('website.welcomeModal.workspaceReady.checkingTitle')
                            ) : (
                                t('website.welcomeModal.workspaceReady.readyTitle')
                            )}
                        </h2>
                        {checking ? (
                            <p className={styles.verticalSubtitle}>
                                {t('website.welcomeModal.workspaceReady.checkingDescription')}
                            </p>
                        ) : null}
                    </div>

                    {!checking && (
                        <>
                            <div className={styles.verticalInfoBox}>
                                <p className={styles.verticalInfoRow}>
                                    {t('website.welcomeModal.workspaceReady.adminPageDescription')}
                                </p>
                                {presentation.showDesktopHandoff ? (
                                    <p className={styles.verticalInfoRow}>
                                        <strong className={styles.boldText}>{t('website.welcomeModal.workspaceReady.desktopName')}</strong>
                                        {t('website.welcomeModal.workspaceReady.desktopDescriptionSuffix')}
                                    </p>
                                ) : null}
                            </div>

                            <div className={styles.verticalImageSection}>
                                <div className={styles.verticalImageWrapper}>
                                    <img src={transitionIllustration} alt={t('website.welcomeModal.workspaceReady.imageAlt')} className={styles.verticalImg} />
                                </div>
                                <div className={styles.verticalImageCaption}>
                                    {workspaceReadyCaption}
                                </div>
                            </div>
                        </>
                    )}

                    <div className={styles.verticalActions}>
                        {checking ? (
                            <div className={styles.verticalLoadingState}>
                                <div className={styles.loadingSpinner} />
                                <span>{t('website.welcomeModal.workspaceReady.waitingInstance')}</span>
                            </div>
                        ) : <WorkspaceReadyDesktopHandoff onClose={onClose} />}
                    </div>
                </div>
                {!checking && (
                    <div className={styles.verticalClose}>
                        <Cross2Icon onClick={onClose} />
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export const InviteSuccessModal: React.FC<WelcomeModalProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const presentation = resolveWorkspaceSuccessPresentation(getCurrentProductCode());

    if (!isOpen) return null;

    return createPortal(
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.verticalModalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.verticalContent}>
                    <div className={styles.verticalHeader}>
                        <h2 className={styles.verticalTitle}>
                            {t('website.welcomeModal.inviteSuccess.title')}
                        </h2>
                    </div>

                    <div className={styles.verticalInfoBox}>
                        <p className={styles.verticalInfoRow}>
                            {t('website.welcomeModal.workspaceReady.adminPageDescription')}
                        </p>
                        {presentation.showDesktopHandoff ? (
                            <p className={styles.verticalInfoRow}>
                                <strong className={styles.boldText}>{t('website.welcomeModal.workspaceReady.desktopName')}</strong>
                                {t('website.welcomeModal.workspaceReady.desktopDescriptionSuffix')}
                            </p>
                        ) : null}
                    </div>

                    <div className={styles.verticalImageSection}>
                        <div className={styles.verticalImageWrapper}>
                            <img src={transitionIllustration} alt={t('website.welcomeModal.workspaceReady.imageAlt')} className={styles.verticalImg} />
                        </div>
                        <div className={styles.verticalImageCaption}>
                            {t(presentation.inviteSuccessCaptionKey)}
                        </div>
                    </div>

                    {presentation.showDesktopHandoff ? (
                        <div className={styles.verticalActions}>
                            <InviteDesktopHandoff onClose={onClose} />
                        </div>
                    ) : null}
                </div>
                <div className={styles.verticalClose}>
                    <Cross2Icon onClick={onClose} />
                </div>
            </div>
        </div>,
        document.body
    );
};
